import type { ReactNode } from 'react'
import { STAGES, hidden, shown, type Alarm, type PlantModel, type Stage } from '../lib/plant'
import { FoldButton, PriorityGlyph, Share, elapsedText, type InspectTarget, type RenderLink } from './plant-parts'

/**
 * The detail row under the plant (docs/fabric-overview.md §Detail panels):
 * the abnormal-conditions list (ISA priority, oldest first), flow today by
 * stream, and routing by extractor type. Props in, no data, no router.
 */

const STAGE_SHORT: Record<Stage, string> = { collection: 'collect', extraction: 'extract', enrichment: 'enrich', 'data-model': 'model', storage: 'store' }
const STAGE_TITLE = Object.fromEntries(STAGES.map((s) => [s.key, s.title])) as Record<Stage, string>

export interface AlarmListProps {
  model: PlantModel
  now: number
  canFollow: boolean
  renderLink: RenderLink
  onInspect: (target: InspectTarget) => void
}

export function AlarmList({ model, now, canFollow, renderLink, onInspect }: AlarmListProps) {
  const row = (a: Alarm) => {
    const body: ReactNode = (
      <>
        <PriorityGlyph priority={a.priority} />
        <span className="pl-al-stage">{STAGE_SHORT[a.stage]}</span>
        <span className="pl-al-msg"><b>{a.subject}</b> · {a.reason}</span>
        <span className="pl-al-age">{a.priority === 'info' ? 'info' : elapsedText(a.ageMs, now)}</span>
      </>
    )
    return (
      <li key={a.id} className="pl-al" data-priority={a.priority}>
        {canFollow ? renderLink(a.link, body, `${a.subject}: ${a.reason} — open ${STAGE_TITLE[a.stage]}`) : <span className="pl-al-plain">{body}</span>}
      </li>
    )
  }
  return (
    <section className="hud-panel pl-panel" aria-labelledby="pl-alarms-title">
      <div className="pl-panel-head">
        <h2 id="pl-alarms-title" className="hud-eyebrow">Abnormal conditions · <b>{model.abnormal}</b>{model.info ? ` + ${model.info} info` : ''}</h2>
        <span className="hud-unit">priority · stage · age</span>
      </div>
      <ul className="pl-al-list">{shown(model.alarms).map(row)}</ul>
      {model.alarms.items.length === 0 ? <p className="hud-unit pl-clear">all clear</p> : null}
      <FoldButton hidden={hidden(model.alarms)} noun="conditions" onInspect={() => onInspect('alarms')} />
    </section>
  )
}

export function FlowPanel({ model, onInspect }: { model: PlantModel; onInspect: (target: InspectTarget) => void }) {
  const c = model.collection
  return (
    <section className="hud-panel pl-panel" aria-labelledby="pl-flow-title">
      <div className="pl-panel-head">
        <h2 id="pl-flow-title" className="hud-eyebrow">Flow today · by stream</h2>
        <span className="hud-unit">daily-stream-details</span>
      </div>
      <ul className="pl-flow-list">
        {shown(c.outlets).map((o) => (
          <li key={o.name} className="pl-flow-row" data-state={o.state}>
            <span className="pl-flow-name">{o.name}</span>
            {o.state === 'ok' ? <Share share={o.share} /> : <span />}
            <span className="pl-flow-n">{o.state === 'unmapped' ? 'no src' : o.eventsToday === null ? '—' : o.eventsToday.toLocaleString()}</span>
          </li>
        ))}
      </ul>
      <FoldButton hidden={hidden(c.outlets)} noun="streams" onInspect={() => onInspect('streams')} />
      <div className="pl-stats">
        <span className="pl-stat"><b>{c.eventsToday === null ? '—' : c.eventsToday.toLocaleString()}</b><span className="hud-unit">events today</span></span>
        <span className="pl-stat"><b>{c.reporting === null ? '—' : `${c.reporting} / ${c.streamsTotal}`}</b><span className="hud-unit">streams reporting</span></span>
      </div>
      <p className="hud-unit">share of today · no hourly series (CB-36)</p>
    </section>
  )
}

export function RoutingPanel({ model, onInspect }: { model: PlantModel; onInspect: (target: InspectTarget) => void }) {
  const e = model.extraction
  const top = e.byType.items[0]?.total ?? 0
  return (
    <section className="hud-panel pl-panel" aria-labelledby="pl-routing-title">
      <div className="pl-panel-head">
        <h2 id="pl-routing-title" className="hud-eyebrow">Routing · by type</h2>
        <span className="hud-unit">parsers/list · enabled / total</span>
      </div>
      <ul className="pl-flow-list">
        {shown(e.byType).map((t) => (
          <li key={t.type} className="pl-flow-row">
            <span className="pl-flow-name">{t.type}</span>
            <Share share={top ? t.total / top : 0} />
            <span className="pl-flow-n">{t.enabled}/{t.total}</span>
          </li>
        ))}
      </ul>
      <FoldButton hidden={hidden(e.byType)} noun="types" onInspect={() => onInspect('types')} />
      <div className="pl-stats">
        <span className="pl-stat"><b>{e.enabled} / {e.total}</b><span className="hud-unit">enabled</span></span>
        <span className="pl-stat"><b>{e.scan.state === 'done' ? e.scan.fallout : '—'}</b><span className="hud-unit">falling to OTHER</span></span>
      </div>
    </section>
  )
}
